import type {
  LostSuperLikelihoodOutput,
  MultipleAccountFeeDragOutput,
} from "./types.js";

// Plain-English result copy for lost-super. Lives in the rules package — not in
// any single app — so every surface (Expo result screen, "my results" list, PDF
// report) renders identical wording from one source. That matters for regulation,
// not just DRY: every string here is INFORMATION, not advice (validation §3.1).
//
// HARD RULE (panel C2): superannuation is an s764A financial product. This copy
// NEVER names, ranks, links, or implies a destination fund to consolidate into;
// every CTA ends at the ATO / myGov free process. No "save $X", no "you'll lose
// $X", no "switch to a low-fee fund", no product recommendation. The fee-drag
// figure is framed as a DUPLICATE-COST illustration with disclosed assumptions,
// and the likelihood is a hedged TYPICAL-CASE band, never a finding about the
// specific user. Drift here is the information → advice kill-threat.

/** Whole-dollar AUD with thousands separators. */
export function formatAud(amount: number): string {
  return `A$${Math.round(amount).toLocaleString("en-AU")}`;
}

/** Round a large AUD figure to "$X.X billion" / "$X million" for the hero stat. */
export function formatBillions(amount: number): string {
  if (amount >= 1_000_000_000) {
    return `$${+(amount / 1_000_000_000).toFixed(1)} billion`;
  }
  if (amount >= 1_000_000) {
    return `$${+(amount / 1_000_000).toFixed(1)} million`;
  }
  return formatAud(amount);
}

/** "7.3 million" / "3.1 million" account counts for the hero stat. */
function formatMillionsCount(count: number): string {
  return `${+(count / 1_000_000).toFixed(1)} million`;
}

// ---------------------------------------------------------------------------
// Surface 1 — lostSuperLikelihood copy
// ---------------------------------------------------------------------------

/**
 * The population hero stat — the ATO aggregate, FY-stamped and ATO-cited. This
 * is the genuinely novel asset; the likelihood band is the secondary nudge.
 */
export function prizeHeadline(out: LostSuperLikelihoodOutput): string {
  const { prize } = out;
  return `The ATO is holding ${formatBillions(prize.totalDollars)} in lost and unclaimed super across about ${formatMillionsCount(prize.accounts)} accounts (as at ${prize.asAt}) — an average of ${formatAud(prize.averageParcel)} per account.`;
}

/**
 * The hedged likelihood line. A TYPICAL-CASE likelihood ("people with a history
 * like yours…"), never a finding that the user specifically has lost super.
 */
export function likelihoodBullet(out: LostSuperLikelihoodOutput): string {
  return `Based on your job, address and name-change history, people with a history like yours are typically at a ${out.bandLabel.toLowerCase()} likelihood of having lost or ATO-held super. This is a general likelihood, not a finding about your specific accounts — only the ATO's record can confirm what (if anything) you have.`;
}

/**
 * The free next action — ALWAYS the ATO/myGov process. Pulled from the output's
 * fixed `freeAction`, which the calc routes through the no-handoff guard.
 */
export function freeActionBullet(out: LostSuperLikelihoodOutput): string {
  return `Free next step: ${out.freeAction.label} This is done in ${out.freeAction.destination} — the search is free and there's nothing to buy.`;
}

/** Context on how much the ATO has already reunited — reassurance, not a pitch. */
export function reunitedBullet(out: LostSuperLikelihoodOutput): string {
  const { prize } = out;
  return `Since 2022 the ATO has reunited or paid out about ${formatBillions(prize.reunitedSince2022Dollars)} across roughly ${formatMillionsCount(prize.reunitedSince2022Accounts)} accounts.`;
}

// ---------------------------------------------------------------------------
// Surface 2 — multipleAccountFeeDrag copy
// ---------------------------------------------------------------------------

/**
 * Headline for the duplicate-cost illustration. Framed as "duplicate accounts
 * cost roughly $X a year" — a cost illustration, never "you'll lose $X".
 */
export function feeDragHeadline(out: MultipleAccountFeeDragOutput): string {
  if (out.duplicateAccounts === 0) {
    return "You told us you hold one super account, so there are no duplicate admin fees or insurance premiums to illustrate.";
  }
  return `Holding ${out.duplicateAccounts} extra super ${out.duplicateAccounts === 1 ? "account" : "accounts"} means paying duplicate admin fees${out.assumptionsUsed.duplicateInsurance ? " and insurance premiums" : ""} of about ${formatAud(out.annualDuplicateCost)} a year, on these assumptions.`;
}

/**
 * The compounding illustration line, with the disclosed assumptions stated
 * inline. Explicitly an illustration; the figure is a "could have grown to," not
 * a prediction or a loss claim.
 */
export function feeDragCompoundedBullet(out: MultipleAccountFeeDragOutput): string {
  if (out.duplicateAccounts === 0) return "";
  const a = out.assumptionsUsed;
  return `As an illustration only: ${formatAud(out.annualDuplicateCost)} a year, if instead it stayed invested and grew at an assumed ${+(a.annualReturn * 100).toFixed(1)}% a year, could compound to about ${formatAud(out.compoundedToRetirement)} over ${out.yearsToRetirement} years (you'd pay ${formatAud(out.totalFeesPaid)} of it as fees along the way). This is a simple compounding illustration on disclosed assumptions, not a prediction of your actual outcome.`;
}

/** Spell out every assumption so the figure is reproducible. */
export function feeDragAssumptionsBullet(out: MultipleAccountFeeDragOutput): string {
  const a = out.assumptionsUsed;
  const parts = [
    `${formatAud(a.adminFeePerAccount)}/year admin fee per duplicate account`,
  ];
  if (a.duplicateInsurance) {
    parts.push(`${formatAud(a.insurancePremiumPerAccount)}/year default insurance premium per duplicate account`);
  }
  parts.push(`an assumed ${+(a.annualReturn * 100).toFixed(1)}% annual net return`);
  return `Assumptions used: ${parts.join("; ")}. Your real fees, premiums and returns will differ — check your own statements.`;
}

/**
 * The free action for the fee-drag surface — identical destination discipline:
 * the ATO/myGov consolidation process, never a fund. Also flags the insurance
 * trap (ATO's own neutral caveat).
 */
export function feeDragFreeActionBullet(): string {
  return "Free next step: you can see all your super and consolidate it yourself in myGov / ATO online services. Check the insurance and any exit fees on each account before you close it — consolidating can cancel insurance you may want to keep.";
}

// ---------------------------------------------------------------------------
// Shared source line
// ---------------------------------------------------------------------------

/** Source-attribution line, composed from the ruleset metadata. */
export function sourceBullet(reportingYear: string, verifiedAt: string): string {
  return `Population figures are from the ATO's lost and unclaimed super release for the ${reportingYear} reporting year, last verified ${verifiedAt}. These figures are updated by the ATO each year — always check ato.gov.au. Educational tool. Not financial advice.`;
}

export function likelihoodExplanationBullets(
  out: LostSuperLikelihoodOutput,
  reportingYear: string,
  verifiedAt: string,
): string[] {
  return [
    prizeHeadline(out),
    likelihoodBullet(out),
    freeActionBullet(out),
    reunitedBullet(out),
    sourceBullet(reportingYear, verifiedAt),
  ];
}

export function feeDragExplanationBullets(
  out: MultipleAccountFeeDragOutput,
  reportingYear: string,
  verifiedAt: string,
): string[] {
  if (out.duplicateAccounts === 0) {
    return [feeDragHeadline(out), sourceBullet(reportingYear, verifiedAt)];
  }
  return [
    feeDragHeadline(out),
    feeDragCompoundedBullet(out),
    feeDragAssumptionsBullet(out),
    feeDragFreeActionBullet(),
    sourceBullet(reportingYear, verifiedAt),
  ];
}
